import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError, formatDate, num, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { M11_ROLES, hasAnyRole } from '../../lib/roles';
import type { Contract, ContractMilestone, ContractPerformanceEvent, ContractVariation } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function ContractDetail() {
  const { id = '' } = useParams();
  const { persona } = useAuth();
  const isOfficer = hasAnyRole(persona?.roles, M11_ROLES.PROCUREMENT_OFFICER);
  const isAnyStaff = hasAnyRole(persona?.roles, ...Object.values(M11_ROLES));

  const contractQ = useApiQuery<Contract>(`/api/v1/m11/contracts/${id}`);
  const milestonesQ = useApiQuery<{ items: ContractMilestone[] }>(`/api/v1/m11/contracts/${id}/milestones`);
  const variationsQ = useApiQuery<{ items: ContractVariation[] }>(`/api/v1/m11/contracts/${id}/variations`);
  const perfQ = useApiQuery<{ items: ContractPerformanceEvent[] }>(isAnyStaff ? `/api/v1/m11/contracts/${id}/performance-events` : null);

  if (contractQ.loading) return <Spinner label="Loading contract…" />;
  if (contractQ.error || !contractQ.data) {
    return (
      <div>
        <Link to="/procurement/contracts" className="text-sm font-semibold text-brand-600 hover:underline">← Back to contracts</Link>
        <div className="mt-4"><ErrorNote message={contractQ.error ?? 'Contract not found'} /></div>
      </div>
    );
  }
  const contract = contractQ.data;

  return (
    <div>
      <Link to="/procurement/contracts" className="mb-3 inline-block text-sm font-semibold text-brand-600 hover:underline">← Back to contracts register</Link>
      <PageHeader title={`Contract ${contract.refNo}`} subtitle={`Award ${contract.awardId} · retention ${contract.retentionPercent}%`} actions={<StatusBadge status={contract.status} />} />

      {contract.docVault.serial ? (
        <div className="card mb-5 p-4 text-xs text-slate-600">
          DocVault execution certificate: <span className="font-mono">{contract.docVault.serial}</span>
          {contract.docVault.verifyUrl ? <> · <a className="text-brand-600 underline" href={contract.docVault.verifyUrl} target="_blank" rel="noreferrer">verify ↗</a></> : null}
        </div>
      ) : null}
      {contract.f12.commitmentId ? (
        <div className="card mb-5 p-4 text-xs text-slate-600">F12 GovFin commitment: {contract.f12.commitmentId}</div>
      ) : null}

      <div className="grid gap-5 lg:grid-cols-2">
        <MilestonesCard contractId={contract.id} isOfficer={isOfficer} milestones={milestonesQ.data?.items ?? []} loading={milestonesQ.loading} error={milestonesQ.error} onChanged={milestonesQ.reload} />
        <VariationsCard contractId={contract.id} isOfficer={isOfficer} variations={variationsQ.data?.items ?? []} loading={variationsQ.loading} error={variationsQ.error} onAdded={variationsQ.reload} />
      </div>

      {isAnyStaff ? (
        <div className="mt-5">
          <PerformanceEventsCard contractId={contract.id} isOfficer={isOfficer} events={perfQ.data?.items ?? []} loading={perfQ.loading} error={perfQ.error} onAdded={perfQ.reload} />
        </div>
      ) : null}
    </div>
  );
}

function MilestonesCard({ contractId, isOfficer, milestones, loading, error, onChanged }: {
  contractId: string; isOfficer: boolean; milestones: ContractMilestone[]; loading: boolean; error: string | null; onChanged: () => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const add = async () => {
    setBusy(true);
    setFormError(null);
    try {
      await api.post(`/api/v1/m11/contracts/${contractId}/milestones`, { title, dueDate });
      setShowForm(false);
      setTitle('');
      onChanged();
    } catch (e) {
      setFormError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not add milestone');
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (milestoneId: string, status: 'MET' | 'MISSED') => {
    try {
      await api.post(`/api/v1/m11/contracts/${contractId}/milestones/${milestoneId}/status`, { status });
      onChanged();
    } catch {
      // surfaced inline below via reload of error-free state; keep simple for a per-row action
    }
  };

  return (
    <div className="card p-5">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-bold text-slate-700">Milestones</h2>
        {isOfficer ? <button className="text-xs font-semibold text-brand-600 hover:underline" onClick={() => setShowForm((s) => !s)}>+ Add</button> : null}
      </div>
      {error ? <ErrorNote message={error} /> : null}
      {showForm ? (
        <div className="mb-3 space-y-2 rounded-lg border border-slate-200 p-3">
          {formError ? <ErrorNote message={formError} /> : null}
          <Field label="Title" className="!mb-0"><input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
          <Field label="Due date" className="!mb-0"><input className="field-input" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></Field>
          <button className="btn-primary !py-1 text-xs" onClick={add} disabled={busy || title.length < 2 || !dueDate}>{busy ? 'Adding…' : 'Add milestone'}</button>
        </div>
      ) : null}
      {loading ? <Spinner /> : milestones.length === 0 ? <p className="text-xs text-slate-400">No milestones recorded.</p> : (
        <ul className="space-y-1.5 text-sm">
          {milestones.map((m) => (
            <li key={m.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
              <div>
                <div>{m.title}</div>
                <div className="text-[11px] text-slate-400">Due {formatDate(m.dueDate)}</div>
              </div>
              <div className="flex items-center gap-1.5">
                <StatusBadge status={m.status} />
                {isOfficer && m.status === 'PENDING' ? (
                  <>
                    <button className="btn-success !px-2 !py-0.5 text-[11px]" onClick={() => setStatus(m.id, 'MET')}>Met</button>
                    <button className="btn-danger !px-2 !py-0.5 text-[11px]" onClick={() => setStatus(m.id, 'MISSED')}>Missed</button>
                  </>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function VariationsCard({ contractId, isOfficer, variations, loading, error, onAdded }: {
  contractId: string; isOfficer: boolean; variations: ContractVariation[]; loading: boolean; error: string | null; onAdded: () => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [description, setDescription] = useState('');
  const [amountDeltaScr, setAmountDeltaScr] = useState('');
  const [approvedBy, setApprovedBy] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const add = async () => {
    setBusy(true);
    setFormError(null);
    try {
      await api.post(`/api/v1/m11/contracts/${contractId}/variations`, { description, amountDeltaScr: Number.parseFloat(amountDeltaScr), approvedBy });
      setShowForm(false);
      setDescription('');
      setAmountDeltaScr('');
      onAdded();
    } catch (e) {
      setFormError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not record variation');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-5">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-bold text-slate-700">Variations (change orders)</h2>
        {isOfficer ? <button className="text-xs font-semibold text-brand-600 hover:underline" onClick={() => setShowForm((s) => !s)}>+ Record</button> : null}
      </div>
      {error ? <ErrorNote message={error} /> : null}
      {showForm ? (
        <div className="mb-3 space-y-2 rounded-lg border border-slate-200 p-3">
          {formError ? <ErrorNote message={formError} /> : null}
          <Field label="Description" className="!mb-0"><input className="field-input" value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Amount delta (SCR, +/-)" className="!mb-0"><input className="field-input" type="number" value={amountDeltaScr} onChange={(e) => setAmountDeltaScr(e.target.value)} /></Field>
            <Field label="Approved by" className="!mb-0"><input className="field-input" value={approvedBy} onChange={(e) => setApprovedBy(e.target.value)} /></Field>
          </div>
          <button className="btn-primary !py-1 text-xs" onClick={add} disabled={busy || description.length < 5 || !amountDeltaScr || approvedBy.length < 2}>{busy ? 'Recording…' : 'Record variation'}</button>
        </div>
      ) : null}
      {loading ? <Spinner /> : variations.length === 0 ? <p className="text-xs text-slate-400">No variations recorded.</p> : (
        <ul className="space-y-1.5 text-sm">
          {variations.map((v) => (
            <li key={v.id} className="rounded-lg bg-slate-50 px-3 py-2">
              <div className="flex justify-between"><span>{v.description}</span><span className="tabular-nums font-semibold">{scr(v.amountDeltaScr)}</span></div>
              <div className="text-[11px] text-slate-400">Approved by {v.approvedBy}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PerformanceEventsCard({ contractId, isOfficer, events, loading, error, onAdded }: {
  contractId: string; isOfficer: boolean; events: ContractPerformanceEvent[]; loading: boolean; error: string | null; onAdded: () => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [type, setType] = useState<'QUALITY_ISSUE' | 'BREACH' | 'COMMENDATION'>('COMMENDATION');
  const [scoreDelta, setScoreDelta] = useState('0');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const add = async () => {
    setBusy(true);
    setFormError(null);
    try {
      await api.post(`/api/v1/m11/contracts/${contractId}/performance-events`, { type, scoreDelta: Number.parseFloat(scoreDelta), notes: notes || undefined });
      setShowForm(false);
      setNotes('');
      onAdded();
    } catch (e) {
      setFormError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not record event');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-5">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-bold text-slate-700">Supplier performance events</h2>
        {isOfficer ? <button className="text-xs font-semibold text-brand-600 hover:underline" onClick={() => setShowForm((s) => !s)}>+ Record</button> : null}
      </div>
      {error ? <ErrorNote message={error} /> : null}
      {showForm ? (
        <div className="mb-3 grid grid-cols-3 gap-2 rounded-lg border border-slate-200 p-3">
          {formError ? <div className="col-span-3"><ErrorNote message={formError} /></div> : null}
          <Field label="Type" className="!mb-0">
            <select className="field-input" value={type} onChange={(e) => setType(e.target.value as typeof type)}>
              <option value="COMMENDATION">Commendation</option>
              <option value="QUALITY_ISSUE">Quality issue</option>
              <option value="BREACH">Breach</option>
            </select>
          </Field>
          <Field label="Score delta" className="!mb-0"><input className="field-input" type="number" value={scoreDelta} onChange={(e) => setScoreDelta(e.target.value)} /></Field>
          <Field label="Notes" className="!mb-0"><input className="field-input" value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
          <div className="col-span-3"><button className="btn-primary !py-1 text-xs" onClick={add} disabled={busy}>{busy ? 'Recording…' : 'Record event'}</button></div>
        </div>
      ) : null}
      {loading ? <Spinner /> : events.length === 0 ? <p className="text-xs text-slate-400">No performance events recorded.</p> : (
        <ul className="space-y-1.5 text-sm">
          {events.map((e) => (
            <li key={e.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
              <div><StatusBadge status={e.type} /> {e.notes ? <span className="ml-2 text-xs text-slate-500">{e.notes}</span> : null}</div>
              <span className={`tabular-nums font-semibold ${num(e.scoreDelta)! < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{num(e.scoreDelta)! > 0 ? '+' : ''}{num(e.scoreDelta)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
